import { Users } from "lucide-react";

interface SameTeamBadgeProps {
  viewerTeamCode: string | null | undefined;
  otherTeamCode: string | null | undefined;
  className?: string;
}

/**
 * Renders a small "Same team" pill when the viewer and another HCP share the
 * same NHS ODS-derived team code. Renders nothing when either side has no
 * team code, or when the codes differ. Never used for anonymous viewers — the
 * server only returns team codes on authenticated endpoints.
 */
export function SameTeamBadge({ viewerTeamCode, otherTeamCode, className }: SameTeamBadgeProps) {
  if (!viewerTeamCode || !otherTeamCode) return null;
  if (viewerTeamCode !== otherTeamCode) return null;
  return (
    <span
      data-testid="badge-same-team"
      title={`Same team (${otherTeamCode})`}
      className={
        "inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 " +
        (className ?? "")
      }
    >
      <Users className="h-3 w-3" />
      Same team
    </span>
  );
}

interface TeamCodeBadgeProps {
  teamCode: string | null | undefined;
  className?: string;
}

/**
 * Renders the team code as a neutral pill. Used to show patients the single
 * team code of the HCP assigned to their request, instead of organisation +
 * site spelled out.
 */
export function TeamCodeBadge({ teamCode, className }: TeamCodeBadgeProps) {
  if (!teamCode) return null;
  return (
    <span
      data-testid="badge-team-code"
      title={`Team ${teamCode}`}
      className={
        "inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 " +
        (className ?? "")
      }
    >
      <Users className="h-3 w-3" />
      Team {teamCode}
    </span>
  );
}
